import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Plus, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Resolution-sida för väntande Classroom-länkningar.
 *
 * När `services/classroom_linker.py` är osäker om vilket prov/läxa ett nytt
 * Classroom-item hör till hamnar det här. Föräldern väljer ett befintligt
 * event eller skapar nytt prov/läxa direkt från itemet.
 *
 * Route: /installningar/classroom-lankar (deep-länkas från push).
 */
export default function ClassroomLinksPage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [createMode, setCreateMode] = useState({});  // pendingId → form-state

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/classroom-links/pending");
      setPending(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte hämta listan");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (pendingId, eventId) => {
    if (!eventId) {
      toast.error("Välj ett prov/läxa först");
      return;
    }
    setBusy({ ...busy, [pendingId]: "resolve" });
    try {
      await api.post(`/classroom-links/${pendingId}/resolve`, { event_id: eventId });
      toast.success("Kopplat");
      setPending(pending.filter((p) => p.id !== pendingId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte koppla");
    } finally {
      setBusy({ ...busy, [pendingId]: null });
    }
  };

  const ignore = async (pendingId) => {
    if (!confirm("Markera som ignorerad? Pingar inte mer om detta material.")) return;
    setBusy({ ...busy, [pendingId]: "ignore" });
    try {
      await api.post(`/classroom-links/${pendingId}/ignore`);
      toast.success("Ignorerat");
      setPending(pending.filter((p) => p.id !== pendingId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte ignorera");
    } finally {
      setBusy({ ...busy, [pendingId]: null });
    }
  };

  const startCreate = (p) => {
    setCreateMode({
      ...createMode,
      [p.id]: {
        category: "homework",
        summary: p.title || p.course_name || "",
        subject: p.course_name || "",
        start: new Date().toISOString().slice(0, 10),
        description: p.snippet || "",
      },
    });
  };

  const submitCreate = async (pendingId) => {
    const form = createMode[pendingId];
    if (!form?.summary?.trim() || !form?.start) {
      toast.error("Titel och datum krävs");
      return;
    }
    setBusy({ ...busy, [pendingId]: "create" });
    try {
      const { data } = await api.post(
        `/classroom-links/${pendingId}/create-event`, form,
      );
      toast.success("Nytt prov/läxa skapat och kopplat");
      setPending(pending.filter((p) => p.id !== pendingId));
      setCreateMode({ ...createMode, [pendingId]: null });
      // Navigera till nytt event om man vill
      console.log("Created event", data.event_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skapa");
    } finally {
      setBusy({ ...busy, [pendingId]: null });
    }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="classroom-links-page">
      <header className="px-4 pt-4 pb-2 flex items-center gap-2">
        <button
          onClick={() => navigate("/installningar")}
          className="p-2 -ml-2 rounded-lg hover:bg-[color:var(--bg-elevated)]"
          aria-label="Tillbaka"
          data-testid="classroom-links-back"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[color:var(--text-tertiary)]">Inställningar</p>
          <h1 className="text-xl font-semibold">Classroom-länkar</h1>
        </div>
      </header>

      <div className="px-4 mt-3">
        <p className="text-xs text-[color:var(--text-tertiary)] mb-3 leading-snug">
          Nya Classroom-inlägg där Edit var osäker på vilket prov/läxa de hör till. Välj rätt event eller skapa nytt — eller ignorera om det inte är relevant.
        </p>

        {loading ? (
          <p className="text-sm text-[color:var(--text-tertiary)]">Läser in…</p>
        ) : pending.length === 0 ? (
          <div className="card-soft p-6 text-center" data-testid="classroom-links-empty">
            <Check className="w-8 h-8 mx-auto mb-2 text-[color:var(--success)]" />
            <p className="text-sm font-medium">Inga väntande länkningar</p>
            <p className="text-xs text-[color:var(--text-tertiary)] mt-1">
              Allt nytt Classroom-material kopplades automatiskt.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {pending.map((p) => {
              const isCreating = !!createMode[p.id];
              const cf = createMode[p.id];
              return (
                <li
                  key={p.id}
                  className="card-soft p-3 space-y-2"
                  data-testid={`classroom-link-pending-${p.id}`}
                >
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-snug">{p.title || "(inget titel)"}</p>
                      {p.link && (
                        <a
                          href={p.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-1 rounded hover:bg-[color:var(--bg-elevated)]"
                          title="Öppna i Classroom"
                          data-testid={`classroom-link-open-${p.id}`}
                        >
                          <ExternalLink size={14} className="text-[color:var(--text-tertiary)]" />
                        </a>
                      )}
                    </div>
                    <p className="text-[11px] text-[color:var(--text-tertiary)] mt-0.5">
                      {p.owner_label && <span className="font-medium">{p.owner_label}</span>}
                      {p.owner_label && p.course_name && " · "}
                      {p.course_name}
                      {p.source && (
                        <span className="ml-1 text-[10px] uppercase tracking-wider opacity-70">
                          · {p.source === "coursework" ? "uppgift" : "inlägg"}
                        </span>
                      )}
                    </p>
                    {p.snippet && (
                      <p className="text-xs text-[color:var(--text-secondary)] mt-1.5 leading-snug">
                        {p.snippet.length > 220 ? p.snippet.slice(0, 220) + "…" : p.snippet}
                      </p>
                    )}
                  </div>

                  {!isCreating ? (
                    <>
                      {p.candidates?.length > 0 ? (
                        <CandidatePicker
                          candidates={p.candidates}
                          onResolve={(eventId) => resolve(p.id, eventId)}
                          disabled={busy[p.id] === "resolve"}
                          pendingId={p.id}
                        />
                      ) : (
                        <p className="text-xs text-[color:var(--text-tertiary)] italic">
                          Inga matchande prov/läxor hittade i kalendern.
                        </p>
                      )}

                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startCreate(p)}
                          disabled={!!busy[p.id]}
                          className="flex-1"
                          data-testid={`classroom-link-create-${p.id}`}
                        >
                          <Plus size={14} className="mr-1" />
                          Skapa nytt
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => ignore(p.id)}
                          disabled={!!busy[p.id]}
                          data-testid={`classroom-link-ignore-${p.id}`}
                        >
                          {busy[p.id] === "ignore" ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <X size={14} />
                          )}
                          <span className="ml-1">Ignorera</span>
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 pt-2 border-t border-[color:var(--border-subtle)]" data-testid={`classroom-link-create-form-${p.id}`}>
                      <Select
                        value={cf.category}
                        onValueChange={(v) => setCreateMode({ ...createMode, [p.id]: { ...cf, category: v } })}
                      >
                        <SelectTrigger className="h-9 text-sm" data-testid={`classroom-link-category-${p.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="homework">Läxa</SelectItem>
                          <SelectItem value="test">Prov</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={cf.summary}
                        onChange={(e) => setCreateMode({ ...createMode, [p.id]: { ...cf, summary: e.target.value } })}
                        placeholder="Titel"
                        className="h-9 text-sm"
                        data-testid={`classroom-link-summary-${p.id}`}
                      />
                      <Input
                        value={cf.subject}
                        onChange={(e) => setCreateMode({ ...createMode, [p.id]: { ...cf, subject: e.target.value } })}
                        placeholder="Ämne (t.ex. Svenska)"
                        className="h-9 text-sm"
                        data-testid={`classroom-link-subject-${p.id}`}
                      />
                      <Input
                        type="date"
                        value={cf.start}
                        onChange={(e) => setCreateMode({ ...createMode, [p.id]: { ...cf, start: e.target.value } })}
                        className="h-9 text-sm"
                        data-testid={`classroom-link-start-${p.id}`}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => submitCreate(p.id)}
                          disabled={busy[p.id] === "create"}
                          className="flex-1"
                          data-testid={`classroom-link-submit-${p.id}`}
                        >
                          {busy[p.id] === "create" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
                          Skapa & koppla
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCreateMode({ ...createMode, [p.id]: null })}
                          data-testid={`classroom-link-create-cancel-${p.id}`}
                        >
                          Avbryt
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function CandidatePicker({ candidates, onResolve, disabled, pendingId }) {
  const [selected, setSelected] = useState(candidates[0]?.event_id || "");
  return (
    <div className="space-y-2">
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger className="h-9 text-sm" data-testid={`classroom-link-candidate-${pendingId}`}>
          <SelectValue placeholder="Välj prov/läxa…" />
        </SelectTrigger>
        <SelectContent>
          {candidates.map((c) => {
            const dt = c.start ? new Date(c.start) : null;
            const dateStr = dt ? dt.toLocaleDateString("sv-SE", { day: "numeric", month: "short" }) : "?";
            return (
              <SelectItem key={c.event_id} value={c.event_id}>
                {c.category === "test" ? "📝 " : "📚 "}
                {dateStr} · {c.summary?.slice(0, 60) || "(utan titel)"}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        onClick={() => onResolve(selected)}
        disabled={disabled || !selected}
        className="w-full"
        data-testid={`classroom-link-resolve-${pendingId}`}
      >
        {disabled ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
        Koppla till valt prov/läxa
      </Button>
    </div>
  );
}
